import * as Sentry from '@sentry/react'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import i18n from '@/i18n'

interface FallbackProps {
  error: unknown
  resetError: () => void
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message
  }
  if (typeof error === 'string') {
    return error
  }
  return i18n.getFixedT(null, 'notifications')('errorUnknown')
}

function ErrorFallback({ error, resetError }: FallbackProps) {
  const { t } = useTranslation('notifications')
  const errorMessage = getErrorMessage(error)

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full text-center">
        <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
          <AlertTriangle className="w-8 h-8 text-red-600" />
        </div>
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">
          {t('errorTitle')}
        </h1>
        <p className="text-gray-600 mb-6">
          {t('errorBody')}
        </p>
        {import.meta.env.DEV && (
          <div className="mb-6 p-4 bg-gray-100 rounded-lg text-left">
            <p className="text-sm font-mono text-red-600 break-all">
              {errorMessage}
            </p>
          </div>
        )}
        <button
          onClick={resetError}
          className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          {t('errorRetry')}
        </button>
      </div>
    </div>
  )
}

interface SentryErrorBoundaryProps {
  children: React.ReactNode
}

export function SentryErrorBoundary({ children }: SentryErrorBoundaryProps) {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <ErrorFallback error={error} resetError={resetError} />
      )}
      onError={(error) => {
        console.error('[Sentry] Error captured by boundary:', error)
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  )
}
